import React, { useEffect, useState } from 'react'
import { BattleArena } from './components/BattleArena'
import { MainMenu } from './components/MainMenu'
import { TestSuite } from './components/TestSuite'
import { DebugPanel } from './components/DebugPanel'
import { FarcasterProvider } from './providers/FarcasterProvider'
import { WalletProvider } from './providers/WalletProvider'
import { GameProvider } from './providers/GameProvider'
import { PlayerPreferencesProvider } from './providers/PlayerPreferencesProvider'
import { TourGuideProvider } from './providers/TourGuideProvider'
import { Splash } from './components/Splash'
import { useFeatureFlags } from './hooks/useFeatureFlags'
import { getAppContext } from './utils/farcaster-detection'
import { ErrorBoundary } from './components/ErrorBoundary'
import ErudaProvider from './providers/ErudaProvider'
import { enableBaseAppDataAttributes, setupBaseAppCanvas, setupBaseAppViewport } from './utils/baseAppCanvas'
import { TourOverlay } from './components/tour/TourOverlay'
import { TourSelectionModal } from './components/modals/TourSelectionModal'
import { useTourGuide } from './providers/TourGuideProvider'
import { BlockchainTourSlides } from './components/tour/BlockchainTourSlides'

// AGC: Tour components that require TourGuideProvider context (v6.9.0)
const TourComponents: React.FC = () => {
  const { state, closeTourSelection } = useTourGuide()
  const [showBlockchainSlides, setShowBlockchainSlides] = React.useState(false)
  
  // AGC: Listen for blockchain tour open event
  React.useEffect(() => {
    const handleOpenBlockchainTour = () => {
      setShowBlockchainSlides(true)
    }
    
    window.addEventListener('blockchain-tour:open', handleOpenBlockchainTour)
    return () => {
      window.removeEventListener('blockchain-tour:open', handleOpenBlockchainTour)
    }
  }, [])
  
  return (
    <>
      {/* AGC: Tour overlay (react-joyride) */}
      <TourOverlay />
      
      {/* AGC: Tour selection modal (for replaying tours) */}
      <TourSelectionModal
        isOpen={state.showTourSelection}
        onClose={closeTourSelection}
      />
      
      {/* AGC: Blockchain Tour Slides - Rendered at App level for proper z-index */}
      <BlockchainTourSlides 
        isOpen={showBlockchainSlides}
        onClose={() => setShowBlockchainSlides(false)}
      />
    </>
  )
}

// AGC: Main App component for Origami Battles
function App() {
  const { isBaseAppContext } = useFeatureFlags()
  const [isLoading, setIsLoading] = useState<boolean>(isBaseAppContext)
  const [currentScreen, setCurrentScreen] = useState<'menu' | 'battle' | 'test'>('menu')
  const [error, setError] = useState<string | null>(null)
  
  // AGC: Track if user is in a battle (for visual warning)
  const isInGame = currentScreen === 'battle'

  // AGC: setFrameReady() now called from MainMenu.tsx when main content loads

  useEffect(() => {
    if (!isBaseAppContext) {
      return
    }

    const disableDataAttr = enableBaseAppDataAttributes()
    const cleanupViewport = setupBaseAppViewport()
    const cleanupCanvas = setupBaseAppCanvas()

    return () => {
      cleanupCanvas()
      cleanupViewport()
      disableDataAttr()
    }
  }, [isBaseAppContext])

  useEffect(() => {
    if (!isBaseAppContext) {
      setIsLoading(false)
      return
    }

    // AGC: Short loading screen for UI initialization
    const timeout = window.setTimeout(() => {
      setIsLoading(false)
    }, 300)

    return () => {
      window.clearTimeout(timeout)
    }
  }, [isBaseAppContext])

  // AGC: Handle navigation between screens
  const handleStartBattle = () => {
    setCurrentScreen('battle')
  }

  const handleBackToMenu = () => {
    setCurrentScreen('menu')
  }

  const handleOpenTestSuite = () => {
    setCurrentScreen('test')
  }

  if (isLoading) {
    return <Splash onComplete={() => setIsLoading(false)} duration={300} fullScreen={true} />
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-red-400 mb-4">Error</h1>
          <p className="text-gray-300">{error}</p>
          <button 
            onClick={() => window.location.reload()} 
            className="mt-4 px-6 py-2 bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors"
          >
            Retry
          </button>
        </div>
      </div>
    )
  }

  return (
    <>
      {isBaseAppContext && <ErudaProvider />}
      <ErrorBoundary>
        <FarcasterProvider>
          <WalletProvider>
            <PlayerPreferencesProvider>
              <TourGuideProvider>
                <GameProvider>
                  <div className="min-h-screen bg-gradient-to-br from-gray-900 via-slate-900 to-gray-800">
                    {/* AGC: Visual warning banner for Base App */}
                    {isBaseAppContext && isInGame && (
                      <div className="fixed top-0 left-0 right-0 z-50 bg-red-600/90 backdrop-blur-sm border-b-2 border-red-400 px-4 py-2 text-center animate-pulse">
                        <p className="text-white font-bold text-sm">
                          ⚠️ DON'T SCROLL TO REFRESH! You'll lose your battle! ⚠️
                        </p>
                      </div>
                    )}
                    {currentScreen === 'menu' && (
                      <MainMenu onStartBattle={handleStartBattle} onOpenTestSuite={handleOpenTestSuite} />
                    )}
                    {currentScreen === 'battle' && <BattleArena onBackToMenu={handleBackToMenu} />}
                    {currentScreen === 'test' && <TestSuite onBackToMenu={handleBackToMenu} />}

                    {/* AGC: Debug Panel - Toggle with Ctrl+D or ` */}
                    <DebugPanel />
                    
                    {/* AGC: Tour Guide Components (v6.9.0) */}
                    <TourComponents />
                  </div>
                </GameProvider>
              </TourGuideProvider>
            </PlayerPreferencesProvider>
          </WalletProvider>
        </FarcasterProvider>
      </ErrorBoundary>
    </>
  )
}

export default App
